// =============================================
// Area Consolidation — merge overlapping fan-out areas before outline
// Runs between fan-out and outline generation
// =============================================

import { parseLLMJSON, getLLMProvider, getStageConfig } from "@/services/llm";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import type { FanOut, MicroArea, AnchorCandidate } from "./queryFanOut";
import { logStage, logIntegrity, logPublic, logDebug, logError } from "./contentLogger";

const MIN_AREAS_FOR_CONSOLIDATION = 5;

/**
 * Hard guard — forbidden SRO passage-type merges. LLM (gpt-4.1-mini) routinely
 * ignores prompt-level zakazy dla comparison ([CRITERIA]↔[SIDE_BY_SIDE]). Ta lista
 * jest enforced code-side: jeśli LLM zaproponuje merge z jedną z par → revert
 * do single-source areas (split back). Wildcard "*" = tag nigdy nie merguje z niczym.
 *
 * Source of truth. Prompt `area_consolidation` w DB to tylko hint — enforcement tutaj.
 */
const FORBIDDEN_MERGE_PAIRS: Array<[string, string]> = [
  ["DIRECT_ANSWER", "*"],
  ["BOUNDARIES", "*"],
  ["PROVENANCE", "ATTRIBUTES"],
  ["EVIDENCE", "COMPONENTS"],
  ["CAUSES", "SYMPTOMS"],
  ["PRICING", "PRODUCT_SPECS"],
  ["CRITERIA", "SIDE_BY_SIDE"],
  ["USE_CASES", "VERDICT"],
  // === KROK 3 glossary+guide (+6) ===
  ["TERM_DEFINITION", "SYNONYMS_AND_VARIANTS"],
  ["TERM_DEFINITION", "COMMON_CONFUSIONS"],
  ["OVERVIEW", "CORE_CONCEPTS"],
  ["PREREQUISITES", "IMPLEMENTATION_PATH"],
  ["CORE_CONCEPTS", "IMPLEMENTATION_PATH"],
  ["COMMON_PITFALLS", "IMPLEMENTATION_PATH"],
];

function extractSroTag(s: string): string | null {
  const m = s.match(/\[([A-Z_]+)\]/);
  return m ? m[1] : null;
}

function isForbiddenMerge(tagA: string, tagB: string): boolean {
  if (!tagA || !tagB) return false;
  if (tagA === tagB) return false;
  for (const [a, b] of FORBIDDEN_MERGE_PAIRS) {
    if (a === "*" || b === "*") {
      const wildcard = a === "*" ? b : a;
      if (tagA === wildcard || tagB === wildcard) return true;
      continue;
    }
    if ((tagA === a && tagB === b) || (tagA === b && tagB === a)) return true;
  }
  return false;
}

interface ConsolidatedArea {
  name: string;
  intent: string;
  question: string;
  merged_from: string[];
  paa: string[];
}

interface ConsolidationResult {
  consolidated: boolean;
  originalCount: number;
  newCount: number;
  mergesMade: number;
  costUsd: number;
  reason: string;
}

/**
 * Post-LLM hard guard: revert forbidden SRO passage-type merges.
 * Returns new consolidated array + rejection count. Same-tag merges passthrough.
 */
function validateMerges(
  consolidated: ConsolidatedArea[],
  origs: MicroArea[],
): { validated: ConsolidatedArea[]; rejections: number } {
  const output: ConsolidatedArea[] = [];
  let rejections = 0;

  const getTagFor = (src: string): string | null => {
    const idx = parseInt(src.match(/^area(\d+)$/i)?.[1] || "0", 10);
    if (idx > 0 && idx <= origs.length) {
      return extractSroTag(origs[idx - 1].area);
    }
    return extractSroTag(src);
  };

  for (const c of consolidated) {
    const mergedFrom = c.merged_from || [];
    if (mergedFrom.length < 2) {
      output.push(c);
      continue;
    }
    const tags = mergedFrom
      .map((src) => getTagFor(src))
      .filter((t): t is string => !!t);

    let rejectedPair: [string, string] | null = null;
    for (let i = 0; i < tags.length && !rejectedPair; i++) {
      for (let j = i + 1; j < tags.length; j++) {
        if (isForbiddenMerge(tags[i], tags[j])) {
          rejectedPair = [tags[i], tags[j]];
          break;
        }
      }
    }

    if (rejectedPair) {
      rejections++;
      logDebug(
        "Consolidation",
        `Merge REJECTED: [${rejectedPair[0]}] <-> [${rejectedPair[1]}] in "${c.name}" — splitting back to ${mergedFrom.length} single-source areas`,
      );
      for (const src of mergedFrom) {
        const idx = parseInt(src.match(/^area(\d+)$/i)?.[1] || "0", 10);
        const orig = idx > 0 && idx <= origs.length ? origs[idx - 1] : undefined;
        if (orig) {
          output.push({
            name: orig.area,
            intent: orig.intent,
            question: orig.question,
            merged_from: [`area${idx}`],
            paa: orig.paa_questions || [],
          });
        } else {
          // Fallback: LLM returned paraphrase (not index ref). Push src verbatim — downstream
          // Jaccard match will try to resolve it; if not, ultimate fallback grabs all PAA.
          output.push({
            name: src,
            intent: c.intent || origs[0]?.intent || "",
            question: c.question,
            merged_from: [src],
            paa: [],
          });
        }
      }
    } else {
      output.push(c);
    }
  }

  return { validated: output, rejections };
}

/**
 * Consolidate overlapping micro_areas in FanOut BEFORE outline generation.
 * Mutates fanOut.micro_areas in place.
 * Only runs if >= MIN_AREAS_FOR_CONSOLIDATION areas.
 */
export async function consolidateAreas(
  fanOut: FanOut,
  canonicalQuestion: string,
  documentRole: string,
  primaryEntity: string,
  mapId?: string,
  nodeId?: string,
): Promise<ConsolidationResult> {
  const originalCount = fanOut.micro_areas.length;

  if (originalCount < MIN_AREAS_FOR_CONSOLIDATION) {
    logPublic("Consolidation", `SKIP: only ${originalCount} areas (threshold=${MIN_AREAS_FOR_CONSOLIDATION})`);
    return { consolidated: false, originalCount, newCount: originalCount, mergesMade: 0, costUsd: 0, reason: "below threshold" };
  }

  const dbPrompt = await loadPrompt("area_consolidation" as any);
  const stageConfig = getStageConfig("area_consolidation" as any);
  const model = dbPrompt?.modelId || stageConfig?.model || "gpt-4.1-mini";
  const provider = dbPrompt?.provider || stageConfig?.provider || "openai";

  // Build areas list for prompt
  const areasListStr = fanOut.micro_areas
    .map((m, i) => `${i + 1}. [${m.intent}] "${m.area}" — "${m.question}"${(m.paa_questions || []).length > 0 ? ` [PAA: ${(m.paa_questions || []).join("; ")}]` : ""}`)
    .join("\n");

  const templateVars: Record<string, string> = {
    canonical_question: canonicalQuestion,
    document_role: documentRole,
    primary_entity: primaryEntity,
    areas_list: areasListStr,
  };

  const prompt = dbPrompt
    ? fillTemplate(dbPrompt.template, templateVars)
    : `Merge overlapping areas for "${canonicalQuestion}" (${documentRole}).\n\nAreas:\n${areasListStr}\n\nReturn JSON: { "consolidated_areas": [...], "merges_made": N, "reason": "..." }`;

  logPublic("Consolidation", `Consolidating ${originalCount} areas`);
  logDebug("Consolidation", "Config", { model, provider, template_loaded: !!dbPrompt });
  logDebug("Consolidation", `Areas BEFORE:\n${areasListStr}`);

  const start = Date.now();
  try {
    const llm = getLLMProvider(provider);
    const response = await llm.call(prompt, {
      model, temperature: 0.1,
      step: "area_consolidation", mapId, nodeId,
    });
    const costUsd = response.costUsd || 0;
    const duration = Date.now() - start;

    const result = parseLLMJSON<{
      consolidated_areas: ConsolidatedArea[];
      merges_made: number;
      reason: string;
    }>(response.text);

    const rawConsolidated = result.consolidated_areas || [];

    // Hard guard: revert forbidden SRO passage-type merges (CRITERIA↔SIDE_BY_SIDE etc.).
    // Prompt v4 ma zakazy jako hint, ale LLM je ignoruje — ten validator jest
    // deterministic source of truth. Rejected merges splituje sie z powrotem do single-source.
    const { validated: consolidated, rejections } = validateMerges(rawConsolidated, fanOut.micro_areas);
    if (rejections > 0) {
      logPublic("Consolidation", `Hard guard: ${rejections} forbidden merge(s) rejected — reverted to originals`);
    }

    if (consolidated.length === 0) {
      logPublic("Consolidation", `LLM returned 0 areas — keeping originals`);
      return { consolidated: false, originalCount, newCount: originalCount, mergesMade: 0, costUsd, reason: "empty result" };
    }

    // Build index for fuzzy matching: normalize area names for lookup
    const normalizeForMatch = (s: string) => s.toLowerCase().replace(/[^a-ząćęłńóśźż0-9 ]/g, "").replace(/\s+/g, " ").trim();
    const areaIndex = fanOut.micro_areas.map((m) => ({
      area: m,
      norm: normalizeForMatch(m.area),
      words: new Set(normalizeForMatch(m.area).split(" ").filter((w) => w.length > 2)),
    }));

    // Build new micro_areas from consolidated result, preserving PAA + fixing intent
    const newMicroAreas: MicroArea[] = consolidated.map((c, idx) => {
      // Collect PAA + resolve intent from merged source areas
      const mergedPaa: string[] = []; // Ignore LLM paa (fabricates "PAA1") — collect only from source areas
      const sourceIntents: string[] = [];
      const matchLog: string[] = [];
      // v9 C1: collect anchor_pool z source areas (dedup case-insensitive, preserve order)
      const mergedAnchorPool: AnchorCandidate[] = [];
      const seenAnchorValues = new Set<string>();
      const pushAnchors = (pool: AnchorCandidate[] | undefined) => {
        for (const a of (pool || [])) {
          const key = (a?.value || "").toLowerCase().trim();
          if (!key || seenAnchorValues.has(key)) continue;
          seenAnchorValues.add(key);
          mergedAnchorPool.push(a);
        }
      };

      for (const sourceName of c.merged_from || []) {
        // 1. Index-based reference: "area2" → originalAreas[1]
        const indexMatch = sourceName.match(/^area(\d+)$/i);
        if (indexMatch) {
          const idx1 = parseInt(indexMatch[1], 10); // 1-based
          const resolved = fanOut.micro_areas[idx1 - 1]; // 0-based
          if (resolved) {
            const paaBefore = mergedPaa.length;
            for (const paa of resolved.paa_questions || []) {
              if (!mergedPaa.includes(paa)) mergedPaa.push(paa);
            }
            sourceIntents.push(resolved.intent);
            pushAnchors(resolved.anchor_pool);
            matchLog.push(`"${sourceName}" → "${resolved.area}" (index ${idx1}, PAA +${mergedPaa.length - paaBefore})`);
            continue;
          }
        }

        const sNorm = normalizeForMatch(sourceName);
        const sWords = new Set(sNorm.split(" ").filter((w) => w.length > 2));

        // 2. Exact normalized match
        let sourceArea = areaIndex.find((a) => a.norm === sNorm)?.area;

        // 3. Substring match
        if (!sourceArea) {
          sourceArea = areaIndex.find(
            (a) => a.norm.includes(sNorm) || sNorm.includes(a.norm)
          )?.area;
        }

        // 4. Jaccard word overlap ≥0.3 (lower threshold — LLM paraphrases merged_from names)
        if (!sourceArea && sWords.size > 0) {
          let bestOverlap = 0;
          let bestCandidate: MicroArea | undefined;
          for (const entry of areaIndex) {
            const overlap = [...sWords].filter((w) => entry.words.has(w)).length;
            const score = overlap / Math.max(sWords.size, entry.words.size);
            if (score > bestOverlap && score >= 0.3) {
              bestOverlap = score;
              bestCandidate = entry.area;
            }
          }
          sourceArea = bestCandidate;
        }

        if (sourceArea) {
          const paaBefore = mergedPaa.length;
          for (const paa of sourceArea.paa_questions || []) {
            if (!mergedPaa.includes(paa)) mergedPaa.push(paa);
          }
          sourceIntents.push(sourceArea.intent);
          pushAnchors(sourceArea.anchor_pool);
          matchLog.push(`"${sourceName}" → "${sourceArea.area}" (PAA +${mergedPaa.length - paaBefore})`);
        } else {
          matchLog.push(`"${sourceName}" → NO MATCH ❌`);
        }
      }

      // 5. Fallback: self-match by consolidated area name
      if (mergedPaa.length === 0 && sourceIntents.length === 0) {
        const cNorm = normalizeForMatch(c.name);
        const selfMatch = areaIndex.find(
          (a) => a.norm === cNorm || a.norm.includes(cNorm) || cNorm.includes(a.norm)
        )?.area;
        if (selfMatch) {
          for (const paa of selfMatch.paa_questions || []) {
            if (!mergedPaa.includes(paa)) mergedPaa.push(paa);
          }
          sourceIntents.push(selfMatch.intent);
          pushAnchors(selfMatch.anchor_pool);
          matchLog.push(`SELF-MATCH: "${c.name}" → "${selfMatch.area}" (PAA +${mergedPaa.length})`);
        }
      }

      // Area without PAA = świadoma decyzja v3 prompt (LLM uznał że żadne PAA nie pasuje
      // semantycznie pod kątem tego SRO tagu). Outline LLM wygeneruje generic H3 dla tej sekcji.
      // Wcześniejszy ULTIMATE FALLBACK aktywnie psuł v3: dodawał PAA z innych areas →
      // duplikował source_paa cross-area → G11 misalignment drop → standalone H2.
      if (mergedPaa.length === 0 && (c.merged_from || []).length > 0) {
        matchLog.push(`ℹ️ Area without PAA — LLM deliberate skip, outline will generate generic H3`);
      }

      logDebug("Consolidation", `PAA resolve "${c.name}": merged_from=${(c.merged_from || []).length}, matched=${matchLog.filter((l) => !l.includes("NO MATCH")).length}, total_paa=${mergedPaa.length}`);
      for (const log of matchLog) {
        logDebug("Consolidation", `  ${log}`);
      }

      // BUG A FIX: Intent from source areas, not from LLM response
      // If all merged sources have same intent → use it. Otherwise → primary intent.
      const uniqueIntents = [...new Set(sourceIntents)];
      const resolvedIntent = uniqueIntents.length === 1 ? uniqueIntents[0]
        : sourceIntents.length > 0 ? sourceIntents[0] // first source = primary
        : c.intent || fanOut.primary_intent.type; // fallback to LLM or primary

      if (c.intent && c.intent !== resolvedIntent) {
        logDebug("Consolidation", `Intent fix: "${c.name}" LLM said "${c.intent}" → resolved to "${resolvedIntent}" (from sources: ${uniqueIntents.join(",")})`);
      }

      // v9 C1: final guard — gdy mergedAnchorPool empty (np. wszystkie source areas
      // miały empty pool, lub no match z merged_from), push PE verbatim. Same invariant
      // co buildAnchorPool: anchor_pool ALWAYS min 1 element gdy primaryEntity != "".
      if (mergedAnchorPool.length === 0 && primaryEntity) {
        mergedAnchorPool.push({ value: primaryEntity, type: "entity", source: "kg_pe" });
      }

      return {
        order: idx + 1,
        intent: resolvedIntent,
        area: c.name,
        question: c.question,
        paa_questions: mergedPaa,
        ymyl: false,
        anchor_pool: mergedAnchorPool.slice(0, 8), // hard cap (same as buildAnchorPool)
      };
    });

    // Log result — LLM's merges_made zawyzalo o `rejections` (rejected splity sa now single-source).
    // Fallback via count difference jest accurate bo newMicroAreas.length reflektuje validator output.
    const llmMergesMade = Math.max(0, (result.merges_made || 0) - rejections);
    const mergesMade = llmMergesMade || (originalCount - newMicroAreas.length);
    logPublic("Consolidation", `Areas AFTER: ${newMicroAreas.length}`);
    for (const a of newMicroAreas) {
      const merged = consolidated.find((c) => c.name === a.area)?.merged_from || [];
      logDebug("Consolidation", `  "${a.area}" [${a.intent}] PAA=${a.paa_questions.length}${a.paa_questions.length > 0 ? ` [${a.paa_questions.join("; ")}]` : ""}${merged.length > 1 ? ` ← merged: ${merged.join(" + ")}` : ""}`);
    }
    logPublic("Consolidation", `${originalCount} → ${newMicroAreas.length} areas (${mergesMade} merges)`);
    logDebug("Consolidation", `Cost & duration`, { cost_usd: costUsd.toFixed(4), duration_ms: duration, reason: result.reason || "n/a" });

    // Integrity check: does primary intent still dominate?
    const primaryType = fanOut.primary_intent.type;
    const primaryCount = newMicroAreas.filter((a) => a.intent === primaryType).length;
    logIntegrity("consolidation_primary_intent_preserved", primaryCount > 0, {
      primary_intent: primaryType,
      primary_areas: primaryCount,
      total_areas: newMicroAreas.length,
      all_intents: newMicroAreas.map((a) => a.intent),
    });

    // Mutate fanOut
    fanOut.micro_areas = newMicroAreas;
    fanOut.primary_intent.micro_areas = newMicroAreas
      .filter((m) => m.intent === fanOut.primary_intent.type)
      .map((m) => m.area);

    return { consolidated: true, originalCount, newCount: newMicroAreas.length, mergesMade, costUsd, reason: result.reason || "" };
  } catch (err) {
    logError("Consolidation", `Error, keeping originals`, err);
    return { consolidated: false, originalCount, newCount: originalCount, mergesMade: 0, costUsd: 0, reason: `error: ${err instanceof Error ? err.message : "unknown"}` };
  }
}
